import _ from 'lodash';
import {
  PRIME_100_COVERAGES,
  PRIME_100_STANDARD_COVERAGE_MAPPING,
} from './constants';
import type { EligibleCoverageValuesResponse } from './types';
import type { P100QuoteDetailsDto } from '../../../../../types/quotes/details/p100-quote-details.dto';
import { Prime100Coverages } from '../types';

export function transformP100CoverageValuesResponse({
  data,
}: {
  data: EligibleCoverageValuesResponse;
}) {
  return _.mapKeys(data, (value, key) => {
    if (key in PRIME_100_STANDARD_COVERAGE_MAPPING) {
      return PRIME_100_STANDARD_COVERAGE_MAPPING[
        key as keyof typeof PRIME_100_STANDARD_COVERAGE_MAPPING
      ];
    }

    return key;
  });
}

const MANDATORY_COVERAGES_FROM_CALCULATOR: Array<{
  name: Prime100Coverages;
  limitKey: string;
}> = [
  {
    name: Prime100Coverages.PCI_FINES_AND_PENALTIES,
    limitKey: 'pciFinesAndPenaltiesLimit',
  },
  {
    name: Prime100Coverages.REGULATOR_DEFENSE_AND_PENALTIES,
    limitKey: 'regulatorDefenceAndPenaltiesLimit',
  },
  {
    name: Prime100Coverages.COURT_ATTENDANCE_COSTS,
    limitKey: 'courtAttendanceCosts',
  },
  { name: Prime100Coverages.SECURITY_BREACH_EXPENSE, limitKey: 'limit' },
  { name: Prime100Coverages.RESTORATION_OF_ELECTRONIC_DATA, limitKey: 'limit' },
  {
    name: Prime100Coverages.REPUTATIONAL_HARM_SUBLIMIT,
    limitKey: 'reputationalHarmSubLimit',
  },
  {
    name: Prime100Coverages.FUNDS_TRANSFER_LIABILITY_SUBLIMIT,
    limitKey: 'fundsTransferLiabilitySubLimit',
  },
  {
    name: Prime100Coverages.SECURITY_BREACH_LIABILITY,
    limitKey: 'fundsTransferLiabilitySubLimit',
  },
];

export function formatP100Coverages({ data }: { data: P100QuoteDetailsDto }) {
  const { initialRequestData = {} as any, calculatorGeneratedData } = data;

  const isV6ContingentQuote =
    initialRequestData?.contingentBusinessIncomeSecurityFailureEndorsement !=
      null ||
    initialRequestData?.contingentBusinessIncomeSystemFailureItEndorsement !=
      null;
  const hasEnhancedFirmoDetails =
    data.firmographicData?.backupFrequency != null ||
    (data.firmographicData?.businessContinuityMeasures?.length ?? 0) > 0;

  const coverages = PRIME_100_COVERAGES.map((coverage) => {
    let isSelectedValue = Boolean(initialRequestData[coverage.isSelectedKey]);

    if (coverage.name === Prime100Coverages.ADDITIONAL_INSURED) {
      isSelectedValue = initialRequestData[coverage.isSelectedKey]?.length > 0;
    }
    if (coverage.name === Prime100Coverages.TRIA) {
      isSelectedValue = !initialRequestData[coverage.isSelectedKey];
    }
    if (
      coverage.name === Prime100Coverages.DEDICATED_BREACH_COSTS_ENDORSEMENT ||
      coverage.name === Prime100Coverages.SERVICE_FRAUD_AND_CRYPTOJACKING
    ) {
      isSelectedValue = Boolean(initialRequestData[coverage.limitKey]);
    }
    if (coverage.name === Prime100Coverages.ADDITIONAL_NAMED_INSURED) {
      isSelectedValue = initialRequestData[coverage.isSelectedKey]?.length > 0;
    }
    if (
      coverage.name === Prime100Coverages.PUBLIC_RELATIONS_EXPENSE ||
      coverage.name === Prime100Coverages.SECURITY_BREACH_LIABILITY
    ) {
      isSelectedValue = true;
    }
    if (
      !isV6ContingentQuote &&
      hasEnhancedFirmoDetails &&
      (coverage.name ===
        Prime100Coverages.ENHANCED_BUSINESS_AND_CONTINGENT_BUSINESS_INCOME ||
        coverage.name ===
          Prime100Coverages.ENHANCED_BUSINESS_AND_CONTINGENT_BUSINESS_INCOME_SYSTEM_FAILURE_INCLUSIVE)
    ) {
      isSelectedValue =
        coverage.name ===
        Prime100Coverages.ENHANCED_BUSINESS_AND_CONTINGENT_BUSINESS_INCOME
          ? Boolean(initialRequestData?.enhancedBusinessAndCbi) &&
            initialRequestData?.enhancedBusinessAndCbiWithSystemFailure !== true
          : Boolean(
              initialRequestData?.enhancedBusinessAndCbiWithSystemFailure
            );
    }

    return {
      name: coverage.name,
      isSelected: isSelectedValue,
      limit: initialRequestData[coverage.limitKey],
      deductible: initialRequestData[coverage.deductibleKey],
      ...(coverage.payload && {
        payload: initialRequestData[coverage.payload],
      }),
    };
  });

  const calcData = calculatorGeneratedData ?? initialRequestData;
  if (calcData && typeof calcData === 'object') {
    MANDATORY_COVERAGES_FROM_CALCULATOR.forEach(({ name, limitKey }) => {
      const limit = calcData[limitKey];
      const limitValue =
        limitKey === 'limit' && typeof limit === 'string'
          ? Number(limit)
          : limit;
      if (limitValue != null && !coverages.some((c) => c.name === name)) {
        coverages.push({
          name,
          isSelected: true,
          limit: limitValue,
          deductible: 0,
          payload: undefined,
        } as (typeof coverages)[number]);
      }
    });
  }

  return {
    ...data,
    coverages,
  };
}

export function deserializeStandardResponse({ data }: { data: any }) {
  return data;
}
